import type { Faq as FaqType } from 'libs/http/api/content/content.types';

import type { AccessRoute } from 'ui/organisms/AccessRoutes/AccessRoutes.types';
import type { ApiEndpoint } from 'ui/molecules/ApiEndpointExplorer/ApiEndpointExplorer.types';
import type { ApiStatusItem } from 'ui/molecules/ApiStatusRibbon/ApiStatusRibbon.types';
import type { CodeToken } from 'ui/molecules/ApiCodeBlock/ApiCodeBlock.types';
import type { ApiTerminalStep } from 'ui/molecules/ApiTerminal/ApiTerminal.types';

// token helpers — keep the response payloads compact + readable
const t = (text: string): CodeToken => ({ text });
const k = (text: string): CodeToken => ({ text, type: 'key' });
const s = (text: string): CodeToken => ({ text, type: 'string' });
const n = (text: string): CodeToken => ({ text, type: 'bool' });
const c = (text: string): CodeToken => ({ text, type: 'comment' });
const kw = (text: string): CodeToken => ({ text, type: 'method' });

const AUTH = "  -H 'Authorization: Token YOUR_API_KEY'";

// ── Hero ────────────────────────────────────────────────────────────────
export const HERO_IDENTS = ['name', 'registration number', 'VAT / EIN', 'website', 'LinkedIn URL'];

export const HERO_STEPS: ApiTerminalStep[] = [
  {
    key: 'search',
    label: '1 · search',
    filename: 'api.globaldatabase.com/v2',
    codeTokens: [
      t('$ curl -X POST '),
      s("'https://api.globaldatabase.com/v2/kyb/search'"),
      t(' \\\n' + AUTH + ' \\\n'),
      t('  -d '),
      s('\'{"location": "GB", "name": "Global Data Intelligence"}\''),
      t('\n\n{\n  '),
      k('"data"'),
      t(': [{\n    '),
      k('"id"'),
      t(': '),
      n('29707645'),
      t(',\n    '),
      k('"name"'),
      t(': '),
      s('"GLOBAL DATA INTELLIGENCE LIMITED"'),
      t(',\n    '),
      k('"country"'),
      t(': '),
      s('"GB"'),
      t(',\n    '),
      k('"registration_number"'),
      t(': '),
      s('"GB 09410808"'),
      t('\n  }]\n}'),
    ],
    results: [{ status: 'success', content: '200 OK — one ID for every endpoint' }],
  },
  {
    key: 'profile',
    label: '2 · profile',
    filename: 'api.globaldatabase.com/v2',
    codeTokens: [
      t('$ curl '),
      s("'https://api.globaldatabase.com/v2/kyb/29707645/lite'"),
      t(' \\\n' + AUTH + '\n\n{\n  '),
      k('"legal_name"'),
      t(': '),
      s('"GLOBAL DATA INTELLIGENCE LIMITED"'),
      t(',\n  '),
      k('"registration_number"'),
      t(': '),
      s('"09410808"'),
      t(',\n  '),
      k('"status"'),
      t(': '),
      s('"Active"'),
      t(',\n  '),
      k('"incorporation_date"'),
      t(': '),
      s('"2015-01-28"'),
      t(',\n  '),
      k('"registered_address"'),
      t(': '),
      s('"Artisans\' House, 7 Queensbridge, Northampton"'),
      t(',\n  '),
      k('"source"'),
      t(': '),
      s('"Companies House"'),
      t('\n}'),
    ],
    results: [{ status: 'success', content: '200 OK — registry-sourced, with attribution' }],
  },
  {
    key: 'fin',
    label: '3 · financials',
    filename: 'api.globaldatabase.com/v2',
    codeTokens: [
      t('$ curl '),
      s("'https://api.globaldatabase.com/v2/kyb/29707645/financials'"),
      t(' \\\n' + AUTH + '\n\n{\n  '),
      k('"statements"'),
      t(': [{\n    '),
      k('"year"'),
      t(': '),
      n('2025'),
      t(',\n    '),
      k('"currency"'),
      t(': '),
      s('"GBP"'),
      t(',\n    '),
      k('"revenue"'),
      t(': '),
      n('…'),
      t(',\n    '),
      k('"net_income"'),
      t(': '),
      n('…'),
      t(',\n    '),
      k('"total_assets"'),
      t(': '),
      n('…'),
      t(',\n    '),
      k('"source_document"'),
      t(': '),
      s('"Annual accounts, filed with Companies House"'),
      t('\n  }]\n}'),
    ],
    results: [{ status: 'success', content: '200 OK — digitised from the original filing' }],
  },
  {
    key: 'group',
    label: '4 · group structure',
    filename: 'api.globaldatabase.com/v2',
    codeTokens: [
      t('$ curl '),
      s("'https://api.globaldatabase.com/v2/kyb/29707645/group-structures/lite'"),
      t(' \\\n' + AUTH + '\n\n[{\n  '),
      k('"id"'),
      t(': '),
      n('29707645'),
      t(',\n  '),
      k('"name"'),
      t(': '),
      s('"GLOBAL DATA INTELLIGENCE LIMITED"'),
      t(',\n  '),
      k('"country"'),
      t(': '),
      s('"GB"'),
      t(',\n  '),
      k('"registration_number"'),
      t(': '),
      s('"GB 09410808"'),
      t(',\n  '),
      k('"selected"'),
      t(': '),
      n('true'),
      t(',\n  '),
      k('"children"'),
      t(': []\n}]'),
    ],
    results: [{ status: 'success', content: '200 OK — parent–subsidiary tree, ready to render' }],
  },
];

// ── Endpoint explorer ─────────────────────────────────────────────────────
export const ENDPOINTS: ApiEndpoint[] = [
  {
    key: 'search',
    method: 'POST',
    path: '/v2/kyb/search',
    description: 'Resolve a company by name, registration number or VAT/EIN — returns the ID used everywhere else.',
    filename: 'POST /v2/kyb/search',
    codeTokens: [
      t('{\n  '),
      k('"data"'),
      t(': [{\n    '),
      k('"id"'),
      t(': '),
      n('29707645'),
      t(',\n    '),
      k('"name"'),
      t(': '),
      s('"GLOBAL DATA INTELLIGENCE LIMITED"'),
      t(',\n    '),
      k('"country"'),
      t(': '),
      s('"GB"'),
      t(',\n    '),
      k('"registration_number"'),
      t(': '),
      s('"GB 09410808"'),
      t('\n  }]\n}\n\n'),
      c('// The ID is your key to every other endpoint.'),
    ],
  },
  {
    key: 'lite',
    method: 'GET',
    path: '/v2/kyb/{id}/lite',
    description: 'Core profile — legal name, status, incorporation date, registered address.',
    filename: 'GET /v2/kyb/{id}/lite',
    codeTokens: [
      t('{\n  '),
      k('"legal_name"'),
      t(': '),
      s('"GLOBAL DATA INTELLIGENCE LIMITED"'),
      t(',\n  '),
      k('"registration_number"'),
      t(': '),
      s('"09410808"'),
      t(',\n  '),
      k('"status"'),
      t(': '),
      s('"Active"'),
      t(',\n  '),
      k('"incorporation_date"'),
      t(': '),
      s('"2015-01-28"'),
      t(',\n  '),
      k('"registered_address"'),
      t(': '),
      s('"Artisans\' House, 7 Queensbridge, Northampton"'),
      t(',\n  '),
      k('"source"'),
      t(': '),
      s('"Companies House"'),
      t('\n}\n\n'),
      c('// Verification in a single call — status, identity, address.'),
    ],
  },
  {
    key: 'officers',
    method: 'GET',
    path: '/v2/kyb/{id}/officers',
    description: 'Directors and officers with appointment dates — for AML checks and representative validation.',
    filename: 'GET /v2/kyb/{id}/officers',
    codeTokens: [
      t('{\n  '),
      k('"data"'),
      t(': [{\n    '),
      k('"id"'),
      t(': '),
      s('"48075764"'),
      t(',\n    '),
      k('"first_name"'),
      t(': '),
      s('"Nicolae"'),
      t(',\n    '),
      k('"last_name"'),
      t(': '),
      s('"Buldumac"'),
      t(',\n    '),
      k('"job_title"'),
      t(': '),
      s('"Director"'),
      t(',\n    '),
      k('"appointed_at"'),
      t(': '),
      s('"2015-01-28"'),
      t('\n  }]\n}\n\n'),
      c('// Validate authorised representatives for AML and EDD.'),
    ],
  },
  {
    key: 'shareholders',
    method: 'GET',
    path: '/v2/kyb/{id}/shareholders',
    description: 'Ownership structure and shareholding — who owns the company, and how much.',
    filename: 'GET /v2/kyb/{id}/shareholders',
    codeTokens: [
      t('{\n  '),
      k('"data"'),
      t(': [{\n    '),
      k('"name"'),
      t(': '),
      s('"…"'),
      t(',\n    '),
      k('"shares_held"'),
      t(': '),
      n('…'),
      t(',\n    '),
      k('"ownership_pct"'),
      t(': '),
      n('…'),
      t(',\n    '),
      k('"type"'),
      t(': '),
      s('"individual | corporate"'),
      t('\n  }]\n}\n\n'),
      c('// Who owns the company, how much — the basis of UBO checks.'),
    ],
  },
  {
    key: 'financials',
    method: 'GET',
    path: '/v2/kyb/{id}/financials',
    description: 'Filed financial statements, digitised and standardised — for credit and risk scoring.',
    filename: 'GET /v2/kyb/{id}/financials',
    codeTokens: [
      t('{\n  '),
      k('"statements"'),
      t(': [{\n    '),
      k('"year"'),
      t(': '),
      n('2025'),
      t(',\n    '),
      k('"currency"'),
      t(': '),
      s('"GBP"'),
      t(',\n    '),
      k('"revenue"'),
      t(': '),
      n('…'),
      t(',\n    '),
      k('"net_income"'),
      t(': '),
      n('…'),
      t(',\n    '),
      k('"total_assets"'),
      t(': '),
      n('…'),
      t(',\n    '),
      k('"source_document"'),
      t(': '),
      s('"Annual accounts, filed with Companies House"'),
      t('\n  }]\n}\n\n'),
      c('// Filed statements, digitised — standardised for credit & risk models.'),
    ],
  },
  {
    key: 'group',
    method: 'GET',
    path: '/v2/kyb/{id}/group-structures/lite',
    description: 'Parent–subsidiary relationships — the corporate tree around the entity.',
    filename: 'GET /v2/kyb/{id}/group-structures/lite',
    codeTokens: [
      t('[{\n  '),
      k('"id"'),
      t(': '),
      n('29707645'),
      t(',\n  '),
      k('"name"'),
      t(': '),
      s('"GLOBAL DATA INTELLIGENCE LIMITED"'),
      t(',\n  '),
      k('"country"'),
      t(': '),
      s('"GB"'),
      t(',\n  '),
      k('"registration_number"'),
      t(': '),
      s('"GB 09410808"'),
      t(',\n  '),
      k('"selected"'),
      t(': '),
      n('true'),
      t(',\n  '),
      k('"children"'),
      t(': []\n}]\n\n'),
      c('// The corporate tree — parents and subsidiaries, ready to render.'),
    ],
  },
];

// ── Language snippets ─────────────────────────────────────────────────────
export interface LangSnippet {
  key: string;
  label: string;
  filename: string;
  codeTokens: CodeToken[];
}

export const LANG_SNIPPETS: LangSnippet[] = [
  {
    key: 'curl',
    label: 'cURL',
    filename: 'terminal',
    codeTokens: [
      t('curl -X POST '),
      s("'https://api.globaldatabase.com/v2/kyb/search'"),
      t(' \\\n' + AUTH + ' \\\n'),
      t('  -H '),
      s("'Content-Type: application/json'"),
      t(' \\\n  -d '),
      s('\'{ "location": "GB", "name": "Global Data Intelligence" }\''),
    ],
  },
  {
    key: 'py',
    label: 'Python',
    filename: 'search.py',
    codeTokens: [
      kw('import'),
      t(' requests\n\nr = requests.post(\n    '),
      s("'https://api.globaldatabase.com/v2/kyb/search'"),
      t(',\n    headers={'),
      s("'Authorization'"),
      t(': '),
      s("'Token YOUR_API_KEY'"),
      t('},\n    json={'),
      s("'location'"),
      t(': '),
      s("'GB'"),
      t(', '),
      s("'name'"),
      t(': '),
      s("'Global Data Intelligence'"),
      t('},\n)\ncompany_id = r.json()['),
      s("'data'"),
      t('][0]['),
      s("'id'"),
      t(']  '),
      c('# → 29707645'),
    ],
  },
  {
    key: 'js',
    label: 'Node.js',
    filename: 'search.js',
    codeTokens: [
      kw('const'),
      t(' res = '),
      kw('await'),
      t(' fetch('),
      s("'https://api.globaldatabase.com/v2/kyb/search'"),
      t(', {\n  method: '),
      s("'POST'"),
      t(',\n  headers: {\n    '),
      s("'Authorization'"),
      t(': '),
      s("'Token YOUR_API_KEY'"),
      t(',\n    '),
      s("'Content-Type'"),
      t(': '),
      s("'application/json'"),
      t(',\n  },\n  body: JSON.stringify({ location: '),
      s("'GB'"),
      t(', name: '),
      s("'Global Data Intelligence'"),
      t(' }),\n});\n\n'),
      kw('const'),
      t(' { data } = '),
      kw('await'),
      t(' res.json();\n'),
      c('// data[0].id → 29707645'),
    ],
  },
];

// ── Status ribbon ─────────────────────────────────────────────────────────
export const STATUS_ITEMS: ApiStatusItem[] = [
  { code: '200', label: 'OK', ok: true },
  { code: '401', label: 'TokenError' },
  { code: '404', label: 'NotFound' },
  { code: '406', label: 'BadRequest' },
  { code: '500', label: 'ServerError' },
];

export const STATUS_NOTE = 'Predictable, named errors — every failure tells you what to fix.';

// ── Why this API ───────────────────────────────────────────────────────────
export const WHY_API_ROUTES: AccessRoute[] = [
  {
    name: 'Registry-sourced, with attribution',
    desc: 'Every record traces to an official government source — Companies House, the Handelsregister, INPI, KVK and 400+ more. Responses carry the source, so your compliance team can audit any field.',
    actions: [],
  },
  {
    name: 'One schema across 200+ countries',
    desc: 'A UK Ltd, a German GmbH and a Delaware C-corp come back in the same structure — statuses, legal forms and financial line items standardised, so one integration covers every jurisdiction.',
    actions: [],
  },
  {
    name: 'Look up by whatever you have',
    desc: 'Name, registration number, VAT or EIN, website or LinkedIn URL — resolve to the same company ID and enrich from any starting point, including a single email domain.',
    actions: [],
  },
  {
    name: 'Built for AI agents too',
    desc: 'The same data is exposed through our MCP server — connect it to Claude, ChatGPT or your own agents, and let them verify companies and pull financials as tools.',
    actions: [],
  },
  {
    name: 'Licensing that lets you ship',
    desc: 'Redistribution and AI-training rights available — embed registry data in your product, resell enriched datasets or train models, with terms designed for it.',
    actions: [],
  },
];

// ── FAQ ───────────────────────────────────────────────────────────────────
export const FAQ_ITEMS: FaqType[] = [
  {
    id: 'auth',
    default_opened: true,
    question: 'How do I authenticate with the Global Database API?',
    answer:
      'With a token in the request header: <code>Authorization: Token YOUR_API_KEY</code>. All requests and responses are JSON over HTTPS.',
  },
  {
    id: 'data',
    question: 'What data can I retrieve through the API?',
    answer:
      'Company profiles, legal status, registered addresses, directors and officers, shareholders and UBOs, group structures, filed financial statements, and enrichment from identifiers like a website or email domain — across 600M+ companies in 200+ countries.',
  },
  {
    id: 'kyb',
    question: 'How does a typical KYB workflow look?',
    answer:
      'Call <code>POST /v2/kyb/search</code> with a name, registration number or VAT/EIN to resolve the company ID, then reuse that ID across the modular endpoints — profile, officers, shareholders, financials, group structure — combining only the checks your workflow needs.',
  },
  {
    id: 'source',
    question: 'Where does the data come from?',
    answer:
      'Directly from official sources — 400+ government registries and regulatory bodies worldwide. Responses carry source attribution, so every field can be traced to the registry it came from.',
  },
  {
    id: 'identifiers',
    question: 'Which identifiers can I use to look up a company?',
    answer:
      'Company name, registration number, VAT or EIN, website or domain, LinkedIn URL — different starting points resolve to the same company record.',
  },
  {
    id: 'financials',
    question: 'Does the API cover financial statements?',
    answer:
      'Yes — filed financial statements for private and listed companies, digitised with OCR and AI and standardised into one taxonomy, with up to 20+ years of history where available.',
  },
  {
    id: 'agents',
    question: 'Can AI agents use the API?',
    answer:
      'Yes. The same data is exposed via our MCP server, so Claude, ChatGPT and custom agents can call company search, verification and financials as tools inside their workflows.',
  },
  {
    id: 'errors',
    question: 'How are errors returned?',
    answer:
      'As named, structured errors — for example <code>TokenVerificationError</code> (401), <code>IdNotFound</code> (404) or <code>InvalidRequest</code> (406) — each with a message and the offending field, so failures are debuggable.',
  },
  {
    id: 'licensing',
    question: 'Can I redistribute the data or train models on it?',
    answer:
      'Yes — licensing includes redistribution and AI-training rights, with flexible terms for embedding registry data in your own products and models.',
  },
  {
    id: 'docs',
    question: 'Where is the full API documentation?',
    answer:
      'At <code>api.globaldatabase.com/docs/v2</code> — complete endpoint references, request and response examples, and authentication guidance.',
  },
];
